class KGPriceSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    try {
      this.min = parseFloat(this.dataset.min);
      if (!Number.isFinite(this.min)) this.min = 0;

      this.max = parseFloat(this.dataset.max);
      if (!Number.isFinite(this.max) || this.max <= this.min) this.max = this.min + 1;

      this.step = parseFloat(this.dataset.step) || 1;
      this.currencySymbol = this.dataset.currencySymbol || '';
      this.currencyIso = this.dataset.currencyIso || 'USD';
      this.locale = (this.dataset.locale || 'en').replace('_', '-');

      this.minValue = this._clamp(parseFloat(this.dataset.minValue), this.min);
      this.maxValue = this._clamp(parseFloat(this.dataset.maxValue), this.max);
      if (this.minValue > this.maxValue) this.minValue = this.maxValue;

      this.rail = this.querySelector('[data-kg-price-rail]');
      this.range = this.querySelector('[data-kg-price-range]');
      this.thumbMin = this.querySelector('[data-kg-price-thumb="min"]');
      this.thumbMax = this.querySelector('[data-kg-price-thumb="max"]');
      this.displayMin = this.querySelector('[data-kg-price-display="min"]');
      this.displayMax = this.querySelector('[data-kg-price-display="max"]');
      this.inputMin = this.querySelector('[data-kg-price-input="min"]');
      this.inputMax = this.querySelector('[data-kg-price-input="max"]');

      if (!this.rail || !this.thumbMin || !this.thumbMax) return;

      try {
        this.formatter = new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currencyIso,
          maximumFractionDigits: 0,
        });
      } catch (e) {
        this.formatter = {
          format: (v) => `${this.currencySymbol}${Number(v).toLocaleString()}`,
        };
      }

      this._bindEvents();
      this._render();
    } catch (err) {
      if (window.console && console.warn) console.warn('[kg-price-slider]', err);
    }
  }

  _clamp(value, fallback) {
    if (!Number.isFinite(value)) return fallback;
    if (value < this.min) return this.min;
    if (value > this.max) return this.max;
    return value;
  }

  _bindEvents() {
    // Pointer (mouse + touch) drag
    this.thumbMin.addEventListener('pointerdown', (e) => this._startDrag(e, 'min'));
    this.thumbMax.addEventListener('pointerdown', (e) => this._startDrag(e, 'max'));

    // Click anywhere on rail moves nearest thumb
    this.rail.addEventListener('pointerdown', (e) => this._railClick(e));

    // Keyboard accessibility
    this.thumbMin.addEventListener('keydown', (e) => this._handleKey(e, 'min'));
    this.thumbMax.addEventListener('keydown', (e) => this._handleKey(e, 'max'));

    // Text input typing/blur
    if (this.inputMin) {
      this.inputMin.addEventListener('input', () => this._onTextInput('min'));
      this.inputMin.addEventListener('blur', () => this._commitText('min'));
      this.inputMin.addEventListener('keydown', (e) => this._handleEnter(e, 'min'));
    }
    if (this.inputMax) {
      this.inputMax.addEventListener('input', () => this._onTextInput('max'));
      this.inputMax.addEventListener('blur', () => this._commitText('max'));
      this.inputMax.addEventListener('keydown', (e) => this._handleEnter(e, 'max'));
    }
  }

  _railClick(event) {
    // Only respond to direct clicks on the rail (not on thumbs)
    if (event.target.closest('[data-kg-price-thumb]')) return;
    const value = this._valueFromEvent(event);
    const distToMin = Math.abs(value - this.minValue);
    const distToMax = Math.abs(value - this.maxValue);
    const which = distToMin <= distToMax ? 'min' : 'max';
    this._setValue(which, value);
    this._render();
    this._syncInputs(true);
    // Continue as a drag from this point
    this._startDrag(event, which);
  }

  _startDrag(event, which) {
    event.preventDefault();
    const thumb = which === 'min' ? this.thumbMin : this.thumbMax;
    try {
      thumb.setPointerCapture(event.pointerId);
    } catch (e) { /* ignore */ }
    thumb.classList.add('is-dragging');
    // Bring the active thumb on top
    this.thumbMin.style.zIndex = which === 'min' ? '5' : '4';
    this.thumbMax.style.zIndex = which === 'max' ? '5' : '4';

    const onMove = (ev) => {
      const value = this._valueFromEvent(ev);
      this._setValue(which, value);
      this._render();
      this._syncInputs(false);
    };

    const onUp = (ev) => {
      try {
        thumb.releasePointerCapture(ev.pointerId);
      } catch (e) { /* ignore */ }
      thumb.classList.remove('is-dragging');
      thumb.removeEventListener('pointermove', onMove);
      thumb.removeEventListener('pointerup', onUp);
      thumb.removeEventListener('pointercancel', onUp);
      // Final sync with submit dispatch
      this._syncInputs(true);
    };

    thumb.addEventListener('pointermove', onMove);
    thumb.addEventListener('pointerup', onUp);
    thumb.addEventListener('pointercancel', onUp);
  }

  _valueFromEvent(event) {
    const rect = this.rail.getBoundingClientRect();
    if (!rect.width) return this.min;
    const x = event.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    const raw = this.min + ratio * (this.max - this.min);
    // Snap to step
    return Math.round(raw / this.step) * this.step;
  }

  _setValue(which, value) {
    const clamped = this._clamp(value, which === 'min' ? this.min : this.max);
    if (which === 'min') {
      this.minValue = Math.min(clamped, this.maxValue);
    } else {
      this.maxValue = Math.max(clamped, this.minValue);
    }
  }

  _handleKey(event, which) {
    let delta = 0;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') delta = -this.step;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') delta = this.step;
    else if (event.key === 'PageDown') delta = -this.step * 10;
    else if (event.key === 'PageUp') delta = this.step * 10;
    else if (event.key === 'Home') {
      event.preventDefault();
      this._setValue(which, this.min);
      this._render();
      this._syncInputs(true);
      return;
    } else if (event.key === 'End') {
      event.preventDefault();
      this._setValue(which, this.max);
      this._render();
      this._syncInputs(true);
      return;
    } else {
      return;
    }
    event.preventDefault();
    const current = which === 'min' ? this.minValue : this.maxValue;
    this._setValue(which, current + delta);
    this._render();
    this._syncInputs(true);
  }

  _handleEnter(event, which) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = which === 'min' ? this.inputMin : this.inputMax;
      if (target) target.blur();
    }
  }

  _onTextInput(which) {
    const target = which === 'min' ? this.inputMin : this.inputMax;
    if (!target) return;
    const value = this._parseUserNumber(target.value);
    if (Number.isNaN(value)) return;
    this._setValue(which, value);
    this._render();
    // Do not write back to the text input while user is typing; native input event will bubble for submit
  }

  _commitText(which) {
    const target = which === 'min' ? this.inputMin : this.inputMax;
    if (!target) return;
    const value = this._parseUserNumber(target.value);
    if (Number.isNaN(value)) {
      // Restore last good value
      target.value = String(which === 'min' ? this.minValue : this.maxValue);
      return;
    }
    this._setValue(which, value);
    this._render();
    // Format the text input to match the clamped value
    target.value = String(which === 'min' ? this.minValue : this.maxValue);
  }

  _render() {
    const span = this.max - this.min || 1;
    const minRatio = (this.minValue - this.min) / span;
    const maxRatio = (this.maxValue - this.min) / span;

    // Thumb positions: left edge of thumb centered at its value position.
    // CSS uses transform: translateX(-50%) so left is the thumb's center.
    this.thumbMin.style.left = `${minRatio * 100}%`;
    this.thumbMax.style.left = `${maxRatio * 100}%`;

    // Range bar spans from min thumb center to max thumb center
    if (this.range) {
      this.range.style.left = `${minRatio * 100}%`;
      this.range.style.right = `${(1 - maxRatio) * 100}%`;
    }

    if (this.displayMin) this.displayMin.textContent = this.formatter.format(this.minValue);
    if (this.displayMax) this.displayMax.textContent = this.formatter.format(this.maxValue);
  }

  _syncInputs(dispatch) {
    if (this.inputMin && document.activeElement !== this.inputMin) {
      const newVal = String(this.minValue);
      if (this.inputMin.value !== newVal) {
        this.inputMin.value = newVal;
        if (dispatch) this.inputMin.dispatchEvent(new Event('input', { bubbles: true }));
      } else if (dispatch) {
        this.inputMin.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    if (this.inputMax && document.activeElement !== this.inputMax) {
      const newVal = String(this.maxValue);
      if (this.inputMax.value !== newVal) {
        this.inputMax.value = newVal;
        if (dispatch) this.inputMax.dispatchEvent(new Event('input', { bubbles: true }));
      } else if (dispatch) {
        this.inputMax.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  _parseUserNumber(str) {
    if (str === undefined || str === null) return NaN;
    const digits = String(str).replace(/\D+/g, '');
    if (digits === '') return NaN;
    return parseInt(digits, 10);
  }
}

if (!customElements.get('kg-price-slider')) {
  customElements.define('kg-price-slider', KGPriceSlider);
}
